/**
 * usePresence Hook
 *
 * Tracks user presence by periodically updating a timestamp on the server.
 * This allows push notifications to be skipped when the user is actively
 * using the app (since they'll see in-app notifications).
 *
 * The hook:
 * - Updates presence immediately when the app becomes focused
 * - Updates presence every 30 seconds while focused
 * - Stops updating when the app loses focus or user logs out
 */

"use client";

import { updatePresence } from "@/actions/presence";
import { useAuth } from "@/contexts/AuthContext";
import { PRESENCE_ENABLED, PRESENCE_UPDATE_INTERVAL } from "@/lib/presence";
import { useCallback, useEffect, useRef } from "react";

export function usePresence(): void {
  const { user } = useAuth();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const stopPresenceUpdates = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startPresenceUpdates = useCallback(() => {
    // Prevent overlapping intervals
    if (intervalRef.current) return;

    // Update immediately
    updatePresence().catch(() => {});

    // Then update periodically
    intervalRef.current = setInterval(() => {
      updatePresence().catch(() => {});
    }, PRESENCE_UPDATE_INTERVAL);
  }, []);

  useEffect(() => {
    if (!PRESENCE_ENABLED || !user) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        startPresenceUpdates();
      } else {
        stopPresenceUpdates();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Start updates if visible
    if (document.visibilityState === "visible") {
      startPresenceUpdates();
    }

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      stopPresenceUpdates();
    };
  }, [user, startPresenceUpdates, stopPresenceUpdates]);
}
